export type NavigationEffectInput = {
  attribute: string;
  document: Document;
  value: string;
  variable: string;
};

export type ResetNavigationEffectInput = Omit<NavigationEffectInput, "value">;

const styleSelector = 'style[data-migrosone-a11y-page-effects="navigation"]';

const pageScope = "body > :not(#migrosone-a11y-widget-root)";

const navigationEffectStyles = `
html[data-migrosone-a11y-navigation-effects][data-migrosone-a11y-highlight-links] ${pageScope} a[href] {
  text-decoration-line: var(--migrosone-a11y-link-text-decoration-line, underline);
  text-decoration-thickness: var(--migrosone-a11y-link-text-decoration-thickness, 0.16em);
  text-underline-offset: var(--migrosone-a11y-link-text-underline-offset, 0.18em);
  outline: var(--migrosone-a11y-link-outline, 0);
  outline-offset: var(--migrosone-a11y-link-outline-offset, 0);
  background: var(--migrosone-a11y-link-background, transparent);
}

html[data-migrosone-a11y-navigation-effects][data-migrosone-a11y-highlight-headings] ${pageScope} h1,
html[data-migrosone-a11y-navigation-effects][data-migrosone-a11y-highlight-headings] ${pageScope} h2,
html[data-migrosone-a11y-navigation-effects][data-migrosone-a11y-highlight-headings] ${pageScope} h3,
html[data-migrosone-a11y-navigation-effects][data-migrosone-a11y-highlight-headings] ${pageScope} h4,
html[data-migrosone-a11y-navigation-effects][data-migrosone-a11y-highlight-headings] ${pageScope} h5,
html[data-migrosone-a11y-navigation-effects][data-migrosone-a11y-highlight-headings] ${pageScope} h6,
html[data-migrosone-a11y-navigation-effects][data-migrosone-a11y-highlight-headings] ${pageScope} [role="heading"] {
  outline: var(--migrosone-a11y-heading-outline, 3px solid #f7c948);
  outline-offset: var(--migrosone-a11y-heading-outline-offset, 3px);
  background: var(--migrosone-a11y-heading-background, transparent);
  box-shadow: var(--migrosone-a11y-heading-box-shadow, none);
}

html[data-migrosone-a11y-navigation-effects][data-migrosone-a11y-focus-emphasis] ${pageScope} :focus-visible {
  outline: var(--migrosone-a11y-focus-outline-width, 4px) solid var(--migrosone-a11y-focus-outline-color, #f7c948);
  outline-offset: var(--migrosone-a11y-focus-outline-offset, 3px);
  box-shadow: 0 0 0 var(--migrosone-a11y-focus-shadow-spread, 2px) rgb(22 35 29 / 35%);
}

html[data-migrosone-a11y-navigation-effects][data-migrosone-a11y-large-cursor] ${pageScope},
html[data-migrosone-a11y-navigation-effects][data-migrosone-a11y-large-cursor] ${pageScope} * {
  cursor: var(--migrosone-a11y-large-cursor, default);
}

html[data-migrosone-a11y-navigation-effects][data-migrosone-a11y-large-cursor] ${pageScope} a[href],
html[data-migrosone-a11y-navigation-effects][data-migrosone-a11y-large-cursor] ${pageScope} button,
html[data-migrosone-a11y-navigation-effects][data-migrosone-a11y-large-cursor] ${pageScope} [role="button"] {
  cursor: var(--migrosone-a11y-large-cursor-pointer, pointer);
}
`;

function canUseDocument(document: Document) {
  return Boolean(document.head && document.body && document.documentElement);
}

function ensureNavigationEffectStyle(document: Document) {
  const existing = document.querySelector(styleSelector);
  if (existing) {
    return true;
  }

  const style = document.createElement("style");
  style.setAttribute("data-migrosone-a11y-page-effects", "navigation");
  style.textContent = navigationEffectStyles;
  document.head.appendChild(style);
  return true;
}

export function applyNavigationEffectValue({
  attribute,
  document,
  value,
  variable,
}: NavigationEffectInput) {
  if (!canUseDocument(document)) {
    return false;
  }

  ensureNavigationEffectStyle(document);
  document.documentElement.style.setProperty(variable, value);
  document.documentElement.setAttribute(attribute, "true");
  document.documentElement.setAttribute(
    "data-migrosone-a11y-navigation-effects",
    "true",
  );
  return true;
}

export function resetNavigationEffectValue({
  attribute,
  document,
  variable,
}: ResetNavigationEffectInput) {
  if (!canUseDocument(document)) {
    return false;
  }

  document.documentElement.style.removeProperty(variable);
  document.documentElement.removeAttribute(attribute);
  return true;
}
